import React, { useRef, useState, Dispatch, SetStateAction, ChangeEvent } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import styled from 'styled-components';
import { Button, CheckboxOld } from 'shared/components';
import { GqlImageBanner, GqlImageBannerPlatform, ImageBannerPlatformType, ImageBannerType } from 'types/graphql';
import { InlineBanner } from 'src/components/inline-banner';
import { useDispensary } from 'src/hooks/use-dispensary';
import useHasMobileApp from 'src/utils/use-has-mobile-app';
import { AssetLibraryModal } from 'src/components/asset-library-modal';
import type { SelectedAsset } from 'src/components/asset-library-modal';
import { PAY_BY_BANK_ALT_TXT } from 'src/dispensary-admin/customize/promotion-popup/promotion-popup';
import { validateURL, validateAlt, displayBannerPlatforms } from '../../image-banner-modal/image-banner-modal.helper';
import ImageBannerPlatform from '../../image-banner-modal/image-banner-platform';
import {
  Block,
  Title,
  Body,
  ImportantText,
  InputContainer,
  Input,
  ValidateCheckIcon,
  PlaceHolderBanner,
  MobilePlaceHolderBanner,
  Separator,
  DesktopBannerImage,
  MobileBannerImage,
} from '../../image-banner-modal/image-banner-modal.styles';
import { ButtonContainer, TwoColumnLayout, LeftColumn, RightColumn } from './configuration-step.styles';
import {
  ConnectedBannerInfo,
  ConnectedBannerLabel,
  ConnectedBannerType,
  PlatformCheckboxContainer,
} from './connected-banner.styles';

type ConfigurationStepProps = {
  banner: GqlImageBanner | undefined;
  currentStepIndex: number;
  setStepNumber: (newStep: number) => void;
  setBannerData: (data: Partial<GqlImageBanner>) => void;
  setIsDirty: Dispatch<SetStateAction<boolean>>;
  handleFile: (e: ChangeEvent<HTMLInputElement>) => Promise<void>;
  isUploadingImage: boolean;
  isUploadingMobileImage: boolean;
};

export const TEST_ID_IMAGE_BANNER_MODAL_ALT_INPUT = 'image-banner-modal-alt-input';
export const TEST_ID_IMAGE_BANNER_MODAL_ALT_INPUT_VALID = 'image-banner-modal-alt-input-valid';

const imgixParams = {
  fit: 'crop',
  crop: 'edges',
};

/**
 * Returns the display label for a connected banner type
 * Uses bannerType to differentiate between regular collections and custom categories
 * since both use connectedType: 'Collection' under the hood
 */
function getConnectedTypeLabel(connectedType: string | null | undefined, bannerType?: ImageBannerType | null): string {
  // Custom categories use connectedType: 'Collection' but bannerType: 'category'
  // This should be changed and removed when we correctly set up the connected type in the backend.
  if (connectedType === 'Collection' && bannerType === ImageBannerType.category) {
    return 'Custom Category';
  }

  switch (connectedType) {
    case 'Special':
      return 'Special';
    case 'Collection':
      return 'Collection';
    case 'Category':
      return 'Category';
    default:
      return 'Item';
  }
}

export function ConfigurationStep({
  banner,
  currentStepIndex,
  setStepNumber,
  setBannerData,
  setIsDirty,
  handleFile: _handleFile,
  isUploadingImage,
  isUploadingMobileImage,
}: ConfigurationStepProps): JSX.Element {
  const flags = useFlags();
  const hasMobileApp = useHasMobileApp();
  const showAssetLibrary = flags['growth.ecomm.asset-library.rollout'] ?? false;
  const dispensary = useDispensary();
  const enterpriseId = dispensary?.retailer?.enterpriseId ?? '';
  const useLibraryFlow = showAssetLibrary && Boolean(enterpriseId);
  const [libraryTarget, setLibraryTarget] = useState<'desktop' | 'mobile' | null>(null);
  const desktopFileInputRef = useRef<HTMLInputElement>(null);
  const mobileFileInputRef = useRef<HTMLInputElement>(null);

  function handleLibrarySelect(asset: SelectedAsset): void {
    if (libraryTarget === 'desktop') {
      setBannerData({ ...banner, image: asset.url });
    } else if (libraryTarget === 'mobile') {
      setBannerData({ ...banner, mobileImage: asset.url });
    }
    setIsDirty(true);
    setLibraryTarget(null);
  }

  function handleSelectImage(target: 'desktop' | 'mobile'): void {
    if (useLibraryFlow) {
      setLibraryTarget(target);
      return;
    }
    if (target === 'desktop') desktopFileInputRef.current?.click();
    else mobileFileInputRef.current?.click();
  }

  const bannerType = banner?.bannerType ?? ImageBannerType.link;
  const isConnectedBanner = bannerType !== ImageBannerType.link;
  const defaultLink = banner?.link ?? '';
  const defaultOpenInNewTab = banner?.openInNewTab ?? true;
  const platforms = banner?.platforms ?? [];
  const image = banner?.image ?? '';
  const mobileImage = banner?.mobileImage ?? '';
  const alt = banner?.alt ?? '';
  const isPayByBankBanner = banner?.alt === PAY_BY_BANK_ALT_TXT;

  function handleSetPlatform(platform: GqlImageBannerPlatform): void {
    const existingPlatforms = [...platforms];
    const existingPlatformIndex = existingPlatforms.findIndex((p) => p?.type === platform.type);

    if (existingPlatformIndex > -1) {
      existingPlatforms[existingPlatformIndex] = platform;
    } else {
      existingPlatforms.push(platform);
    }

    setBannerData({ ...banner, platforms: existingPlatforms });
    setIsDirty(true);
  }

  /**
   * Toggle platform enabled status for connected banners (simpler than full platform config)
   */
  function handleTogglePlatformEnabled(platformType: ImageBannerPlatformType, enabled: boolean): void {
    const existingPlatforms = [...platforms];
    const existingPlatformIndex = existingPlatforms.findIndex((p) => p?.type === platformType);

    const platform: GqlImageBannerPlatform = {
      enabled,
      link: null,
      openInNewTab: true,
      type: platformType,
    };

    if (existingPlatformIndex > -1) {
      existingPlatforms[existingPlatformIndex] = platform;
    } else {
      existingPlatforms.push(platform);
    }

    setBannerData({ ...banner, platforms: existingPlatforms });
    setIsDirty(true);
  }

  function handleSetAlt(e: ChangeEvent<HTMLInputElement>): void {
    setBannerData({ ...banner, alt: e.target.value });
    setIsDirty(true);
  }

  function handleNext(): void {
    setStepNumber(currentStepIndex + 2);
  }

  const webPlatform = platforms.find((p) => p?.type === ImageBannerPlatformType.web);
  const kioskPlatform = platforms.find((p) => p?.type === ImageBannerPlatformType.kiosk);
  const iosPlatform = platforms.find((p) => p?.type === ImageBannerPlatformType.iosMobileApp);

  const requiresDesktopImage = (webPlatform?.enabled ?? true) || (kioskPlatform?.enabled ?? true);
  const requiresMobileImage = (webPlatform?.enabled ?? true) || ((iosPlatform?.enabled ?? true) && hasMobileApp);

  const platformLinksValid = platforms.every((p) => !p?.enabled || !p.link || validateURL(p.link ?? ''));

  const isNextDisabled =
    isUploadingImage ||
    isUploadingMobileImage ||
    (requiresDesktopImage && !image) ||
    (requiresMobileImage && !mobileImage) ||
    (!requiresMobileImage && !requiresDesktopImage) ||
    (!isConnectedBanner && !platformLinksValid);

  /**
   * Render platform selection for connected banner types
   * Shows only enabled/disabled toggles without URL inputs
   */
  function renderConnectedPlatforms(): JSX.Element {
    return (
      <>
        <ConnectedBannerInfo>
          <ConnectedBannerType>
            Connected to {getConnectedTypeLabel(banner?.connectedType, banner?.bannerType)}
          </ConnectedBannerType>
          <ConnectedBannerLabel>
            When clicked, customers will be directed to the{' '}
            {getConnectedTypeLabel(banner?.connectedType, banner?.bannerType).toLowerCase()} page.
          </ConnectedBannerLabel>
        </ConnectedBannerInfo>

        <Block>
          <Title>Platforms</Title>
          <Body>Select the platforms where this image banner should be displayed.</Body>
        </Block>

        {!requiresDesktopImage && !requiresMobileImage && (
          <Block>
            <InlineBanner description='At least one platform required to create image banner.' severity='error' />
          </Block>
        )}

        <PlatformCheckboxContainer>
          <CheckboxOld
            checked={webPlatform?.enabled ?? true}
            id='web-platform'
            label='Web'
            name='web-platform'
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleTogglePlatformEnabled(ImageBannerPlatformType.web, e.target.checked);
            }}
            pt='0'
            width='100%'
          />
        </PlatformCheckboxContainer>

        {!isPayByBankBanner && (
          <PlatformCheckboxContainer>
            <CheckboxOld
              checked={kioskPlatform?.enabled ?? true}
              id='kiosk-platform'
              label='Kiosk'
              name='kiosk-platform'
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleTogglePlatformEnabled(ImageBannerPlatformType.kiosk, e.target.checked);
              }}
              pt='0'
              width='100%'
            />
          </PlatformCheckboxContainer>
        )}

        {hasMobileApp && (
          <PlatformCheckboxContainer>
            <CheckboxOld
              checked={iosPlatform?.enabled ?? true}
              id='ios-platform'
              label='Mobile App'
              name='ios-platform'
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleTogglePlatformEnabled(ImageBannerPlatformType.iosMobileApp, e.target.checked);
              }}
              pt='0'
              width='100%'
            />
          </PlatformCheckboxContainer>
        )}
      </>
    );
  }

  /**
   * Renders the left column content based on banner type and feature flags
   */
  function renderLeftColumnContent(): JSX.Element {
    // Connected banners show simplified platform selection
    if (isConnectedBanner) {
      return renderConnectedPlatforms();
    }

    // Platform-specific configuration
    return (
      <>
        <Block>
          <Title>Platforms</Title>
          <Body>
            Select the platforms where the image banner should be displayed, and include a link if applicable. When the
            banner image is clicked, the customer wil be directed to the link provided per platform.
          </Body>
        </Block>

        {!requiresDesktopImage && !requiresMobileImage && (
          <Block>
            <InlineBanner description='At least one platform required to create image banner.' severity='error' />
          </Block>
        )}

        {Object.values(displayBannerPlatforms).map((item) => {
          const isWebPlatform = item.value === ImageBannerPlatformType.web;
          const platform: GqlImageBannerPlatform = platforms.find((p) => p?.type === item.value) ?? {
            enabled: true,
            link: isWebPlatform ? defaultLink : null,
            openInNewTab: isWebPlatform ? defaultOpenInNewTab : true,
            type: item.value,
          };

          if (platform.type === ImageBannerPlatformType.iosMobileApp && !hasMobileApp) {
            return null;
          }

          // Hide the kiosk platform checkbox/option for Pay by Bank banners
          if (isPayByBankBanner && platform.type === ImageBannerPlatformType.kiosk) {
            return null;
          }

          return (
            <ImageBannerPlatform
              key={item.value}
              description={item.description}
              label={item.label}
              linkBehaviorDescription={item.linkBehaviorDescription}
              platform={platform}
              setPlatform={handleSetPlatform}
            />
          );
        })}
      </>
    );
  }

  return (
    <>
      <TwoColumnLayout>
        <LeftColumn>{renderLeftColumnContent()}</LeftColumn>

        <RightColumn>
          {requiresDesktopImage && (
            <>
              {image ? (
                <DesktopBannerImage
                  imgixParams={{ ...imgixParams, ar: '6:2', fill: 'blur' }}
                  src={image}
                  htmlAttributes={{ alt }}
                />
              ) : (
                <PlaceHolderBanner>Desktop image preview will show here.</PlaceHolderBanner>
              )}

              <Block>
                <Title>Desktop Image</Title>
                <Body>Recommended size: 2400px (width) × 800px (height). JPEG, PNG, or GIF formats.</Body>
                <LibraryButton type='button' onClick={() => handleSelectImage('desktop')}>
                  {image ? 'CHANGE IMAGE' : 'SELECT IMAGE'}
                </LibraryButton>
                {!useLibraryFlow && (
                  <input
                    ref={desktopFileInputRef}
                    id='image-banner-file-input'
                    type='file'
                    accept='image/*'
                    onChange={_handleFile}
                    style={{ display: 'none' }}
                  />
                )}
              </Block>
            </>
          )}

          {requiresMobileImage && (
            <>
              {mobileImage ? (
                <MobileBannerImage
                  imgixParams={{ ...imgixParams, ar: '2.22:1', fill: 'blur' }}
                  src={mobileImage}
                  htmlAttributes={{ alt }}
                />
              ) : (
                <MobilePlaceHolderBanner>Mobile image preview will show here.</MobilePlaceHolderBanner>
              )}

              <Block>
                <Title>Mobile image</Title>
                <Body>
                  Uploaded images should be 800px (width) 360px (height), and in JPEG, PNG, or GIF formats.
                  {(iosPlatform?.enabled ?? true) && hasMobileApp && (
                    <div>
                      <br />
                      <ImportantText>Important: </ImportantText>
                      If using this image for your mobile app, to comply with App Store guidelines, do not upload images
                      that include vape products such as disposables, pods, cartridges, and vaporizers themselves. This
                      may result in your app being rejected.
                    </div>
                  )}
                </Body>
                <LibraryButton type='button' onClick={() => handleSelectImage('mobile')}>
                  {mobileImage ? 'CHANGE IMAGE' : 'SELECT IMAGE'}
                </LibraryButton>
                {!useLibraryFlow && (
                  <input
                    ref={mobileFileInputRef}
                    id='mobile-image-banner-file-input'
                    type='file'
                    accept='image/*'
                    onChange={_handleFile}
                    style={{ display: 'none' }}
                  />
                )}
              </Block>
            </>
          )}

          {(requiresDesktopImage || requiresMobileImage) && (
            <>
              <Separator />

              <Block>
                <Title>Image Alt Text</Title>
                <Body>Describe your image to improve accessibility and SEO.</Body>
                <InputContainer>
                  <Input
                    type='text'
                    value={alt}
                    onChange={handleSetAlt}
                    data-testid={TEST_ID_IMAGE_BANNER_MODAL_ALT_INPUT}
                  />
                  <ValidateCheckIcon
                    valid={validateAlt(alt) ? 1 : 0}
                    data-testid={TEST_ID_IMAGE_BANNER_MODAL_ALT_INPUT_VALID}
                  />
                </InputContainer>
              </Block>
            </>
          )}
        </RightColumn>
      </TwoColumnLayout>

      <ButtonContainer>
        <Button onClick={handleNext} disabled={isNextDisabled}>
          Next
        </Button>
      </ButtonContainer>

      {useLibraryFlow && libraryTarget !== null && (
        <AssetLibraryModal
          enterpriseId={enterpriseId}
          isOpen
          onClose={() => setLibraryTarget(null)}
          onSelect={handleLibrarySelect}
          title={libraryTarget === 'mobile' ? 'Select Mobile Image from Library' : 'Select Desktop Image from Library'}
          description='Choose an asset from your enterprise library to use as a banner image'
        />
      )}
    </>
  );
}

const LibraryButton = styled.button`
  height: 30px;
  padding: 0 16px;
  margin-top: 10px;
  font-size: 12px;
  font-weight: 700;
  background-color: #0b99e6;
  color: #ffffff;
  border: none;
  border-radius: 33px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: #165070;
  }
`;
